/**
 * Session state for the guest (shared-listing) area.
 *
 * Entirely independent of AuthContext: a guest has no account, no roles, and no
 * member token. Keeping the two apart means a signed-in member and a verified
 * guest can share a browser without either invalidating the other.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  clearGuestSession,
  getGuestToken,
  GUEST_EMAIL_KEY,
  GUEST_NAME_KEY,
  GUEST_SESSION_ENDED_EVENT,
  storeGuestSession,
} from "@/lib/guestApi";

interface GuestContextValue {
  token: string | null;
  email: string | null;
  name: string | null;
  isVerified: boolean;
  startSession: (token: string, email: string, name: string | null) => void;
  endSession: () => void;
}

const GuestContext = createContext<GuestContextValue>({
  token: null,
  email: null,
  name: null,
  isVerified: false,
  startSession: () => {},
  endSession: () => {},
});

export function useGuest() {
  return useContext(GuestContext);
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function GuestProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getGuestToken());
  const [email, setEmail] = useState<string | null>(() => read(GUEST_EMAIL_KEY));
  const [name, setName] = useState<string | null>(() => read(GUEST_NAME_KEY));

  const endSession = useCallback(() => {
    clearGuestSession();
    setToken(null);
    setEmail(null);
    setName(null);
  }, []);

  const startSession = useCallback((next: string, nextEmail: string, nextName: string | null) => {
    storeGuestSession(next, nextEmail, nextName);
    setToken(next);
    setEmail(nextEmail);
    setName(nextName);
  }, []);

  // guestApi clears storage the moment the API rejects a session; mirror that
  // into React state so the UI re-prompts instead of rendering a dead page.
  useEffect(() => {
    const onEnded = () => {
      setToken(null);
      setEmail(null);
      setName(null);
    };
    window.addEventListener(GUEST_SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(GUEST_SESSION_ENDED_EVENT, onEnded);
  }, []);

  const value = useMemo<GuestContextValue>(
    () => ({ token, email, name, isVerified: Boolean(token), startSession, endSession }),
    [token, email, name, startSession, endSession],
  );

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
}
